/**
 * PartnerInvoice (netting de settlements) — constantes runtime.
 *
 * Ref: SETTLEMENTS_NETTING.md §4. Misma razón que `partnerCollectStatus.ts` en
 * sama-admin: los `.d.ts` no emiten JS, así que los enums numéricos viven aquí.
 *
 * Estados: DRAFT=1, ISSUED=2, PENDING_PARTNER_PAYMENT=3, PENDING_SAMA_PAYOUT=4,
 * SETTLED=5, DISPUTED=6, VOID=7.
 * Dirección: PAYOUT=1 (neto a favor del partner), COLLECT=2 (a favor de Sama),
 * ZERO=3 (neto cero, se liquida sin transferencia).
 */
export const PARTNER_INVOICE_STATUS = {
  DRAFT: 1,
  ISSUED: 2,
  PENDING_PARTNER_PAYMENT: 3,
  PENDING_SAMA_PAYOUT: 4,
  SETTLED: 5,
  DISPUTED: 6,
  VOID: 7,
} as const;

export type PartnerInvoiceStatusValue =
  (typeof PARTNER_INVOICE_STATUS)[keyof typeof PARTNER_INVOICE_STATUS];

export const PARTNER_INVOICE_STATUS_LABELS: Record<number, string> = {
  1: 'Borrador',
  2: 'Recién cerrada',
  3: 'Paga el aliado',
  4: 'Paga Sama',
  5: 'Lista',
  6: 'En revisión',
  7: 'Anulada',
};

export const PARTNER_INVOICE_STATUS_DESCRIPTIONS: Record<number, string> = {
  1: 'Vista previa del cierre. Aún no cuenta, se recalcula al cerrar el día.',
  2: 'Ya cerramos el día. Está lista para pagarse.',
  3: 'El aliado le paga a Sama. Estamos esperando el reporte del pago.',
  4: 'Sama le paga al aliado. Estamos preparando la transferencia.',
  5: 'Lista. Ya no se debe nada de este día.',
  6: 'La estamos revisando. Las ventas quedan intactas mientras tanto.',
  7: 'Anulada. Las ventas volvieron a estar disponibles.',
};

export const PARTNER_INVOICE_STATUS_COLORS: Record<number, string> = {
  1: 'default',
  2: 'cyan',
  3: 'orange',
  4: 'blue',
  5: 'green',
  6: 'red',
  7: 'default',
};

export const PARTNER_INVOICE_DIRECTION = {
  PAYOUT: 1,
  COLLECT: 2,
  ZERO: 3,
} as const;

export type PartnerInvoiceDirectionValue =
  (typeof PARTNER_INVOICE_DIRECTION)[keyof typeof PARTNER_INVOICE_DIRECTION];

export const PARTNER_INVOICE_DIRECTION_LABELS: Record<number, string> = {
  1: 'Cobra el aliado',
  2: 'Cobra Sama',
  3: 'Quedan a mano',
};

export const PARTNER_INVOICE_DIRECTION_DESCRIPTIONS: Record<number, string> = {
  1: 'Sama le transfiere el resultado al aliado.',
  2: 'El aliado le transfiere el resultado a Sama.',
  3: 'Lo que entra y sale se compensa: no hay transferencia.',
};

/**
 * El partner nunca ve DRAFT: es preview recalculable del admin. Doble capa con
 * el filtro en `getPartnerInvoices` — este guard cubre navegación directa a
 * `/facturas/:id` con un borrador.
 */
export const isInvoiceVisibleToPartner = (status: number): boolean =>
  status !== PARTNER_INVOICE_STATUS.DRAFT;

/** Solo un DRAFT se puede emitir. Fuera de DRAFT el backend da 400. */
export const canIssueInvoice = (status: number): boolean => status === PARTNER_INVOICE_STATUS.DRAFT;

/**
 * Anular libera las ventas (`invoiceId = null`, → AVAILABLE). En la práctica
 * solo aplica a DRAFT: con pierna de pago el backend da 400 y hay que cancelar
 * la pierna (eso ya anula la factura). Ref: partner-invoice-payments.md §7.1.
 */
export const canVoidInvoice = (status: number): boolean => status === PARTNER_INVOICE_STATUS.DRAFT;

/** Pedir revisión solo mueve la factura, no toca las ventas. */
export const canDisputeInvoice = (status: number): boolean =>
  status === PARTNER_INVOICE_STATUS.PENDING_PARTNER_PAYMENT ||
  status === PARTNER_INVOICE_STATUS.PENDING_SAMA_PAYOUT;

export const canResolveDispute = (status: number): boolean =>
  status === PARTNER_INVOICE_STATUS.DISPUTED;

/**
 * Reportar pago solo aplica al lado COLLECT: el partner informa que transfirió
 * a Sama. El lado PAYOUT lo paga Sama, el partner solo lo audita.
 */
export const canReportInvoicePayment = (status: number, direction: number): boolean =>
  direction === PARTNER_INVOICE_DIRECTION.COLLECT &&
  status === PARTNER_INVOICE_STATUS.PENDING_PARTNER_PAYMENT;

/**
 * PayoutStatus (Sama → partner). No hay CANCELLED: cancelar un Payout lo deja
 * en FAILED. El PartnerPayout usa otros valores (`PARTNER_PAYOUT_STATUS` de
 * cada app).
 */
export const PAYOUT_STATUS = {
  PENDING: 1,
  PROCESSING: 2,
  COMPLETED: 3,
  FAILED: 4,
  PENDING_RECONCILIATION: 5,
} as const;

export const PAYOUT_STATUS_LABELS: Record<number, string> = {
  1: 'Pendiente',
  2: 'En proceso',
  3: 'Completada',
  4: 'Fallida',
  5: 'En conciliación',
};

/** Signo del neto para colorear: > 0 partner, < 0 Sama, 0 compensado. */
export const getInvoiceNetSign = (netAmountLocal: number): 'partner' | 'sama' | 'zero' => {
  if (netAmountLocal > 0) return 'partner';
  if (netAmountLocal < 0) return 'sama';
  return 'zero';
};
